import type { RiskPolicy } from "@gram/client/models/components/riskpolicy.js";
import type { ShadowMCPInventoryServer } from "@gram/client/models/components/shadowmcpinventoryserver.js";

type OriginalPolicy = Pick<RiskPolicy, "action" | "enabled" | "sources">;

/** Default disposition of a shadow MCP blocking policy. block_all denies every
 * server unless allowed (the original behavior); allow_all permits every
 * server unless it appears on the policy's blocked-URL list. Immutable after
 * create. */
export type ShadowMCPDisposition = "block_all" | "allow_all";

export function isShadowMCPBlockConfiguration(
  sources: readonly string[],
  action: string,
): boolean {
  return action === "block" && sources.includes("shadow_mcp");
}

export function isBlockingShadowMCPPolicy(
  enabled: boolean,
  sources: readonly string[],
  action: string,
): boolean {
  return enabled && isShadowMCPBlockConfiguration(sources, action);
}

/** Why an existing allow_all policy's Shadow MCP detector and Deny action
 *  cannot be turned off. Its blocked-server list only means something while
 *  the policy detects Shadow MCP and denies, so the server refuses an edit
 *  that retires that posture; the editor says so up front rather than letting
 *  the save fail. */
export const SHADOW_MCP_ALLOW_ALL_LOCK_REASON =
  "This policy allows every Shadow MCP server except the ones on its blocked list, so it has to keep detecting Shadow MCP and denying. Create a separate policy to enforce something else.";

/** Whether an edit is bound by that rule. Only a saved allow_all policy that
 *  is still blocking is: a block_all policy's server list is a set of
 *  exceptions to a deny and retires with it, and a new policy has no list. */
export function shadowMCPAllowAllPostureLocked(
  policy:
    | (OriginalPolicy & { shadowMcpDisposition?: string | undefined })
    | null,
): boolean {
  if (!policy || policy.shadowMcpDisposition !== "allow_all") return false;
  return isShadowMCPBlockConfiguration(policy.sources, policy.action);
}

export function shadowMCPAllowedURLsForMutation({
  action,
  selectedCategories,
  selectedURLs,
  originalPolicy,
  disposition = "block_all",
}: {
  action: string;
  selectedCategories: ReadonlySet<string>;
  selectedURLs: ReadonlySet<string>;
  originalPolicy: OriginalPolicy | null;
  disposition?: ShadowMCPDisposition;
}): string[] | undefined {
  // Allowed URLs are a block_all concept; an allow_all policy carries a
  // blocked list instead (shadowMCPBlockedURLsForMutation) and the server
  // rejects allowed URLs on it.
  if (disposition === "allow_all") return undefined;

  const targetIsShadowMCPBlock = isBlockingShadowMCPPolicy(
    true,
    [...selectedCategories],
    action,
  );
  if (targetIsShadowMCPBlock) return [...selectedURLs].sort();

  if (
    originalPolicy &&
    isShadowMCPBlockConfiguration(originalPolicy.sources, originalPolicy.action)
  ) {
    return [];
  }

  return undefined;
}

export function shadowMCPBlockedURLsForMutation({
  action,
  selectedCategories,
  selectedURLs,
  disposition,
}: {
  action: string;
  selectedCategories: ReadonlySet<string>;
  selectedURLs: ReadonlySet<string>;
  disposition: ShadowMCPDisposition;
}): string[] | undefined {
  // The blocked list only exists on allow_all blocking shadow MCP policies.
  // There is no clear-on-morph branch like the allowed-URL helper has: the
  // server refuses to take an allow_all policy out of the blocking posture
  // (see SHADOW_MCP_ALLOW_ALL_LOCK_REASON), so one can never stop being one.
  if (disposition !== "allow_all") return undefined;
  const targetIsShadowMCPBlock = isBlockingShadowMCPPolicy(
    true,
    [...selectedCategories],
    action,
  );
  if (!targetIsShadowMCPBlock) return undefined;
  return [...selectedURLs].sort();
}

export function shadowMCPSelectionIsDirty(
  targetIsShadowMCPBlock: boolean,
  selectedURLs: ReadonlySet<string>,
  originalURLs: ReadonlySet<string> | null,
): boolean {
  if (!targetIsShadowMCPBlock || originalURLs === null) return false;
  if (selectedURLs.size !== originalURLs.size) return true;

  for (const url of selectedURLs) {
    if (!originalURLs.has(url)) return true;
  }
  return false;
}

export function shadowMCPSelectionIsInitialized(
  targetIsShadowMCPBlock: boolean,
  initializedEditorIdentity: string | null,
  editorIdentity: string,
): boolean {
  return (
    !targetIsShadowMCPBlock || initializedEditorIdentity === editorIdentity
  );
}

export interface ShadowMCPDecisionConflict {
  canonicalServerUrl: string;
  serverName?: string;
  decision: "approved" | "denied";
}

/**
 * The standing review decisions this edit's URL toggles contradict. Mirrors
 * the server's conflict check so the confirm dialog opens before the save
 * round-trips; the server independently rejects an unconfirmed contradicting
 * save, so a miss here degrades to an error toast, never a silent
 * supersession.
 */
export function shadowMCPDecisionConflicts({
  servers,
  originalURLs,
  selectedURLs,
  disposition,
}: {
  servers: readonly ShadowMCPInventoryServer[];
  originalURLs: ReadonlySet<string> | null;
  selectedURLs: ReadonlySet<string>;
  disposition: ShadowMCPDisposition;
}): ShadowMCPDecisionConflict[] {
  if (originalURLs === null) return [];

  const conflicts: ShadowMCPDecisionConflict[] = [];
  for (const server of servers) {
    const decision = standingDecisionOf(server.approvalRequest);
    if (decision === undefined) continue;

    const url = server.canonicalServerUrl;
    const has = originalURLs.has(url);
    const wants = selectedURLs.has(url);
    if (has === wants) continue;

    // On an allow list (block_all) removing revokes access and adding grants
    // it; on a block list (allow_all) the directions invert.
    let removingAllow = has && !wants;
    if (disposition === "allow_all") removingAllow = !removingAllow;

    const contradicted =
      (removingAllow && decision === "approved") ||
      (!removingAllow && decision === "denied");
    if (!contradicted) continue;

    conflicts.push({
      canonicalServerUrl: url,
      serverName: server.serverName,
      decision,
    });
  }

  return conflicts.sort((a, b) =>
    a.canonicalServerUrl.localeCompare(b.canonicalServerUrl),
  );
}

/**
 * The decision still standing for a review, independent of lifecycle status
 * (a reopened request's prior decision keeps enforcing). Prefers the
 * server-computed standing_decision, falling back to status for a server one
 * release behind.
 */
function standingDecisionOf(
  request: ShadowMCPInventoryServer["approvalRequest"],
): "approved" | "denied" | undefined {
  if (!request) return undefined;
  if (
    request.standingDecision === "approved" ||
    request.standingDecision === "denied"
  ) {
    return request.standingDecision === "approved" ? "approved" : "denied";
  }
  if (request.status === "approved" || request.status === "denied") {
    return request.status === "approved" ? "approved" : "denied";
  }
  return undefined;
}

export function shadowMCPSelectionBaselineForUpdate(body: {
  shadowMcpAllowedUrls?: readonly string[];
  shadowMcpBlockedUrls?: readonly string[];
}): Set<string> | undefined {
  // The two lists are disposition-exclusive, so at most one key is present.
  if (Object.prototype.hasOwnProperty.call(body, "shadowMcpBlockedUrls")) {
    return new Set(body.shadowMcpBlockedUrls ?? []);
  }
  if (!Object.prototype.hasOwnProperty.call(body, "shadowMcpAllowedUrls")) {
    return undefined;
  }
  return new Set(body.shadowMcpAllowedUrls ?? []);
}
